import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trustAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/shared/index';
import toast from 'react-hot-toast';

// ── Constants (mirror backend) ────────────────────────────────────────────────
const LEVELS = [
  { name: 'New',     min: 0,    max: 99,       orderLimit: 399,      color: '#9ca3af', bg: '#f9fafb', perks: ['Orders up to ₹399', 'Build trust with each rental', 'UPI payments'] },
  { name: 'Trusted', min: 100,  max: 299,       orderLimit: 999,      color: '#0277bd', bg: '#e3f2fd', perks: ['Orders up to ₹999', 'Full catalogue access', 'Priority support queue'] },
  { name: 'Silver',  min: 300,  max: 599,       orderLimit: 2499,     color: '#5e35b1', bg: '#ede7f6', perks: ['Orders up to ₹2499', '5% discount on every rental', 'Extended return window'] },
  { name: 'Gold',    min: 600,  max: 999,       orderLimit: 4999,     color: '#b8860b', bg: '#fffbeb', perks: ['Orders up to ₹4999', '10% discount on every rental', 'Free delivery on all orders'] },
  { name: 'Elite',   min: 1000, max: Infinity,  orderLimit: Infinity, color: '#1b5e20', bg: '#e8f5e9', perks: ['Unlimited orders', '15% discount on every rental', 'VIP badge on profile'] },
];

const POINT_EVENTS = [
  { event: 'FIRST_RENTAL_COMPLETE', label: 'First successful rental',  points: '+100', icon: '★' },
  { event: 'RENTAL_COMPLETE',       label: 'On-time return + good condition', points: '+50', icon: '★' },
  { event: 'EARLY_RETURN',          label: 'Returning item early',     points: '+15', icon: '★' },
  { event: 'REVIEW_ITEM',           label: 'Writing a product review', points: '+10', icon: '★' },
  { event: 'COLLEGE_VERIFIED',      label: 'College email verified',   points: '+25', icon: '★' },
  { event: 'REFERRAL_SUCCESS',      label: 'Successful referral',      points: '+50', icon: '★' },
  { event: 'LATE_RETURN_PER_DAY',   label: 'Late return (per day)',    points: '−20', icon: '▼' },
  { event: 'ITEM_DAMAGED',          label: 'Item returned damaged',    points: '−50', icon: '▼' },
  { event: 'LAST_MIN_CANCEL',       label: 'Last-minute cancellation', points: '−30', icon: '▼' },
];

function getLevel(points) {
  return LEVELS.find(l => points >= l.min && points <= l.max) || LEVELS[0];
}
function getNextLevel(points) {
  const idx = LEVELS.findIndex(l => points >= l.min && points <= l.max);
  return idx < LEVELS.length - 1 ? LEVELS[idx + 1] : null;
}

// ── Level Badge ───────────────────────────────────────────────────────────────
function LevelBadge({ level, large }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: large ? '6px 18px' : '3px 10px',
      fontSize: large ? 11 : 9,
      fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
      color: large ? '#fff' : level.color,
      background: large ? level.color : level.bg,
      border: large ? 'none' : `1px solid ${level.color}44`,
    }}>{level.name}</span>
  );
}

// ── Circular progress ─────────────────────────────────────────────────────────
function CircularProgress({ points, level, nextLevel }) {
  const pct   = nextLevel ? Math.min(100, ((points - level.min) / (nextLevel.min - level.min)) * 100) : 100;
  const r     = 54;
  const circ  = 2 * Math.PI * r;
  const dash  = circ - (pct / 100) * circ;

  return (
    <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
      <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
        <circle cx="70" cy="70" r={r} fill="none"
          stroke="var(--gold)" strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={dash}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, color: 'white', lineHeight: 1 }}>{points}</div>
        <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>Points</div>
      </div>
    </div>
  );
}

// ── Re-Verify Form (for expired/expiring users) ───────────────────────────────
function ReVerifyForm({ user, onVerified }) {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp,     setOtp]     = useState('');

  const sendOtp = async () => {
    setLoading(true);
    try {
      await trustAPI.verifyCollege({ collegeEmail: user.collegeEmail });
      setOtpSent(true);
      toast.success(`Code sent to ${user.collegeEmail}`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send code.'); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    if (otp.length !== 6) { toast.error('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      await trustAPI.confirmCollege({ otp });
      toast.success('College re-verified for another 6 months!');
      onVerified();
    } catch (e) { toast.error(e.response?.data?.message || 'Incorrect code.'); }
    finally { setLoading(false); }
  };

  if (!otpSent) return (
    <button onClick={sendOtp} disabled={loading}
      style={{ padding: '9px 20px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
      {loading ? 'Sending...' : `Send Code to ${user.collegeEmail}`}
    </button>
  );

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <input className="input" placeholder="6-digit code" value={otp}
        onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
        style={{ width: 130, letterSpacing: '0.28em', textAlign: 'center', fontSize: 18 }}
        inputMode="numeric" autoFocus />
      <button onClick={confirm} disabled={loading || otp.length !== 6}
        style={{ padding: '9px 20px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
        {loading ? 'Verifying...' : 'Confirm'}
      </button>
    </div>
  );
}

// ── College Verification Section ──────────────────────────────────────────────
function CollegeVerify({ user, onVerified }) {
  const [email,   setEmail]   = useState('');
  const [otp,     setOtp]     = useState('');
  const [step,    setStep]    = useState('email'); // email | otp
  const [loading, setLoading] = useState(false);
  const [college, setCollege] = useState('');

  const sendOtp = async () => {
    if (!email.includes('@')) { toast.error('Enter a valid college email.'); return; }
    setLoading(true);
    try {
      const { data } = await trustAPI.verifyCollege({ collegeEmail: email });
      setCollege(data.college);
      setStep('otp');
      toast.success(`Code sent to ${email}`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed.'); }
    finally { setLoading(false); }
  };

  const confirmOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      const { data } = await trustAPI.confirmCollege({ otp });
      toast.success(data.message);
      try { localStorage.setItem('college_verified_celebrate', 'true'); } catch {}
      onVerified();
    } catch (e) { toast.error(e.response?.data?.message || 'Incorrect code.'); }
    finally { setLoading(false); }
  };

  // Compute expiry state
  const expiresAt   = user?.collegeExpiresAt ? new Date(user.collegeExpiresAt) : null;
  const now         = new Date();
  const isExpired   = user?.isCollegeVerified === false && user?.collegeEmail; // was verified, now expired
  const isActive    = user?.isCollegeVerified && expiresAt && expiresAt > now;
  const daysLeft    = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : 0;
  const expiringSoon = isActive && daysLeft <= 14;
  const fmtDate     = d => d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  if (isActive && !expiringSoon) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
        <div style={{ width: 32, height: 32, background: '#2d6a2d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1b5e20' }}>College Verified — {user.college}</div>
          <div style={{ fontSize: 11, color: '#2d6a2d', marginTop: 2 }}>Valid until {fmtDate(expiresAt)} · {daysLeft} days remaining</div>
        </div>
      </div>
    );
  }

  if (expiringSoon) {
    return (
      <div style={{ border: '1px solid #f59e0b', background: '#fffbeb', padding: '14px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 32, height: 32, background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>Verification Expiring — {daysLeft} days left</div>
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>{user.college} · Expires {fmtDate(expiresAt)}</div>
          </div>
        </div>
        <ReVerifyForm user={user} onVerified={onVerified} />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div style={{ border: '1px solid #b71c1c', background: '#ffebee', padding: '14px 18px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#b71c1c', marginBottom: 6 }}>College Verification Expired</div>
        <div style={{ fontSize: 11, color: '#c62828', marginBottom: 14 }}>
          Your verification for {user.collegeEmail} expired. Orders are now limited to ₹399 until you re-verify.
        </div>
        <ReVerifyForm user={user} onVerified={onVerified} />
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--border)', padding: '18px' }}>
      <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 14 }}>
        College Verification — +25 Points
      </div>

      {step === 'email' ? (
        <>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.7 }}>
            Enter your college email (e.g. <strong>2023csb001@nitj.ac.in</strong>). We'll send a 6-digit code to verify you're an enrolled student.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              className="input" placeholder="yourname@nitj.ac.in"
              value={email} onChange={e => setEmail(e.target.value.toLowerCase())}
              style={{ flex: 1, minWidth: 200, fontSize: 13 }}
              onKeyDown={e => e.key === 'Enter' && sendOtp()}
            />
            <button onClick={sendOtp} disabled={loading}
              style={{ padding: '10px 20px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
              {loading ? 'Sending...' : 'Send Code'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            Code sent to <strong>{email}</strong> ({college})
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="input" placeholder="6-digit code"
              value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ width: 140, letterSpacing: '0.3em', textAlign: 'center', fontSize: 18 }}
              inputMode="numeric" autoFocus
              onKeyDown={e => e.key === 'Enter' && confirmOtp()}
            />
            <button onClick={confirmOtp} disabled={loading || otp.length !== 6}
              style={{ padding: '10px 20px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button onClick={() => setStep('email')}
              style={{ fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Change email
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TrustScorePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [trust,     setTrust]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [tab,       setTab]       = useState('overview');

  const fetchTrust = async () => {
    try {
      const { data } = await trustAPI.getMyTrust();
      setTrust(data.trust);
    } catch { toast.error('Could not load trust score.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTrust(); }, []);

  const handleRedeem = async () => {
    if (!window.confirm('Redeem 1000 points for a ₹100 Amazon voucher?')) return;
    setRedeeming(true);
    try {
      const { data } = await trustAPI.redeem();
      setTrust(data.trust);
      toast.success(`Voucher ${data.voucher.code} created! Valid for 90 days.`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to redeem.'); }
    finally { setRedeeming(false); }
  };

  if (loading) return <Spinner size="lg" centered />;

  const points   = trust?.points || 0;
  const level    = getLevel(points);
  const nextLevel = getNextLevel(points);
  const ptsToNext = nextLevel ? nextLevel.min - points : 0;
  const activeVouchers = trust?.vouchers?.filter(v => v.status === 'active') || [];

  const TABS = ['overview', 'history', 'vouchers', 'how it works'];

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <div style={{ background: 'var(--ink)', borderBottom: '3px solid var(--gold)', padding: 'clamp(28px,4vw,48px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8, opacity: 0.7 }}>Your</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,6vw,52px)', color: 'white', fontWeight: 300, lineHeight: 1, marginBottom: 20 }}>Warba Score</h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
            <CircularProgress points={points} level={level} nextLevel={nextLevel} />
            <div>
              <LevelBadge level={level} large />
              {trust?.isBanned ? (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#b71c1c22', border: '1px solid #b71c1c', fontSize: 12, color: '#ef9a9a' }}>
                  Account suspended — {trust.banReason}
                </div>
              ) : nextLevel ? (
                <>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 12, marginBottom: 8 }}>
                    <strong style={{ color: 'var(--gold)' }}>{ptsToNext} points</strong> to reach {nextLevel.name}
                  </div>
                  {/* Progress bar */}
                  <div style={{ width: 'clamp(160px,40vw,260px)', height: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 2, width: `${Math.min(100, ((points - level.min) / (nextLevel.min - level.min)) * 100)}%`, transition: 'width 1s ease' }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                    {level.min} — {nextLevel.min - 1} pts
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 10 }}>Maximum level reached</div>
              )}

              {/* Quick stats */}
              <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
                {[
                  ['Rentals', trust?.totalRentals || 0],
                  ['Returns', trust?.successfulReturns || 0],
                  ['Vouchers', activeVouchers.length],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: '#c9a84c', fontWeight: 400 }}>{val}</div>
                    <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', overflowX: 'auto', padding: '0 clamp(16px,4vw,40px)' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '14px 20px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: 'none', border: 'none', borderBottom: `2px solid ${tab === t ? 'var(--gold)' : 'transparent'}`,
              color: tab === t ? 'var(--ink)' : 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: "'Josefin Sans', sans-serif", transition: 'all 0.15s',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(20px,4vw,32px) clamp(16px,4vw,40px)' }}>

        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Current level perks */}
            <div style={{ background: 'white', border: '1px solid var(--border)', borderTop: `3px solid ${level.color}`, padding: '20px 22px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)' }}>Your Current Perks</div>
                <LevelBadge level={level} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {level.perks.map((perk, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                    <div style={{ width: 20, height: 20, background: level.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={level.color} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span style={{ color: 'var(--ink)' }}>{perk}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Redeem voucher */}
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>Redeem Points</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: 'var(--ink)', marginBottom: 4 }}>
                    1000 pts = ₹100 Amazon Voucher
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    You have <strong style={{ color: points >= 1000 ? '#2d6a2d' : 'var(--ink)' }}>{points} points</strong>
                    {points >= 1000
                      ? <span style={{ color: '#2d6a2d', marginLeft: 8 }}>— Ready to redeem!</span>
                      : <span style={{ color: 'var(--muted)', marginLeft: 8 }}>— {1000 - points} more needed</span>
                    }
                  </div>
                </div>
                <button onClick={handleRedeem} disabled={points < 1000 || redeeming || trust?.isBanned}
                  style={{
                    padding: '11px 24px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                    background: points >= 1000 ? 'var(--ink)' : 'var(--cream-dark)',
                    color: points >= 1000 ? 'var(--cream)' : 'var(--muted)',
                    border: 'none', cursor: points >= 1000 ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
                  }}>
                  {redeeming ? 'Processing...' : 'Redeem Voucher'}
                </button>
              </div>
              {/* Redemption progress bar */}
              {points < 1000 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ height: 6, background: 'var(--cream-dark)', borderRadius: 3 }}>
                    <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 3, width: `${(points / 1000) * 100}%`, transition: 'width 1s ease' }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6 }}>{points} / 1000 points</div>
                </div>
              )}
            </div>

            {/* College verification */}
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>College Verification</div>
              <CollegeVerify user={user} onVerified={async () => { await refreshUser(); fetchTrust(); }} />
            </div>

            {/* Level map */}
            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>Level Roadmap</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {LEVELS.map((l, i) => {
                  const isActive = l.name === level.name;
                  const isPast   = points > l.max;
                  return (
                    <div key={l.name} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: i < LEVELS.length - 1 ? '1px solid var(--cream-dark)' : 'none', opacity: isPast && !isActive ? 0.5 : 1 }}>
                      {/* Dot */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 2 }}>
                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: isActive ? l.color : isPast ? l.color + '44' : 'var(--cream-dark)', border: isActive ? `2px solid ${l.color}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isPast && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={l.color} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                          {isActive && <div style={{ width: 6, height: 6, borderRadius: '50%', background: l.color }} />}
                        </div>
                        {i < LEVELS.length - 1 && <div style={{ width: 2, height: 20, background: 'var(--cream-dark)', marginTop: 4 }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? l.color : 'var(--ink)' }}>{l.name}</span>
                          <LevelBadge level={l} />
                          <span style={{ fontSize: 10, color: 'var(--muted)' }}>{l.min}{l.max < Infinity ? `–${l.max}` : '+'} pts</span>
                          {isActive && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: l.color, background: l.bg, padding: '2px 8px' }}>Current</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{l.perks.join(' · ')}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === 'history' && (
          <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>Points History</div>
            {!trust?.history?.length ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 13 }}>No activity yet. Start renting to earn points.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {trust.history.map((h, i) => {
                  const isPositive = h.points > 0;
                  const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < trust.history.length - 1 ? '1px solid var(--cream-dark)' : 'none', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>{h.note || h.event}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{fmt(h.createdAt)}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isPositive ? '#2d6a2d' : '#b71c1c', flexShrink: 0 }}>
                        {isPositive ? '+' : ''}{h.points}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* VOUCHERS TAB */}
        {tab === 'vouchers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Active vouchers */}
            {activeVouchers.length > 0 && (
              <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
                <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>Active Vouchers</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {activeVouchers.map((v, i) => {
                    const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', background: 'var(--cream)', border: '1px dashed var(--gold)', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--ink)', marginBottom: 4 }}>{v.code}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Expires {fmt(v.expiresAt)} · Amazon Gift Voucher</div>
                        </div>
                        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: 'var(--gold)', fontWeight: 300 }}>₹{v.value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Redeem */}
            <div style={{ background: 'white', border: '1px solid var(--border)', borderTop: '2px solid var(--gold)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Redeem Points</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginBottom: 8, fontWeight: 300 }}>1000 pts → ₹100 Amazon Voucher</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.7 }}>
                Vouchers are valid for 90 days from the date of redemption. You currently have <strong>{points} points</strong>.
              </div>
              <button onClick={handleRedeem} disabled={points < 1000 || redeeming || trust?.isBanned}
                style={{ padding: '11px 28px', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: points >= 1000 ? 'var(--ink)' : 'var(--cream-dark)', color: points >= 1000 ? 'var(--cream)' : 'var(--muted)', border: 'none', cursor: points >= 1000 ? 'pointer' : 'not-allowed' }}>
                {redeeming ? 'Processing...' : `Redeem (${Math.floor(points / 1000)} available)`}
              </button>
            </div>

            {/* Used vouchers */}
            {trust?.vouchers?.filter(v => v.status !== 'active').length > 0 && (
              <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px', opacity: 0.6 }}>
                <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 14 }}>Used / Expired</div>
                {trust.vouchers.filter(v => v.status !== 'active').map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '8px 0', borderBottom: '1px solid var(--cream-dark)' }}>
                    <span style={{ fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.08em', textDecoration: 'line-through' }}>{v.code}</span>
                    <span style={{ color: 'var(--muted)', textTransform: 'uppercase', fontSize: 10 }}>{v.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HOW IT WORKS TAB */}
        {tab === 'how it works' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>How You Earn Points</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {POINT_EVENTS.map((e, i) => {
                  const isPositive = e.points.startsWith('+');
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < POINT_EVENTS.length - 1 ? '1px solid var(--cream-dark)' : 'none', gap: 12 }}>
                      <div style={{ fontSize: 13, color: 'var(--ink)' }}>{e.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isPositive ? '#2d6a2d' : '#b71c1c', flexShrink: 0 }}>{e.points}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: 'white', border: '1px solid var(--border)', padding: '20px 22px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>Trust & Safety</div>
              <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 2 }}>
                Your Warba Score reflects how trustworthy a renter you are. Consistent good behaviour unlocks better perks and pricing. Repeated late returns, damage, or fraud will reduce your score.
              </div>
              <div style={{ marginTop: 16, padding: '14px 16px', background: '#ffebee', border: '1px solid #b71c1c22' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#b71c1c', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Fraud Policy</div>
                <div style={{ fontSize: 12, color: '#c62828', lineHeight: 1.8 }}>
                  Any attempt to defraud Warbadobe — including not returning items, filing false damage claims, or payment disputes — will result in permanent account ban, recovery of full item value via UPI/bank details on record, and legal action under applicable Indian law.
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}